import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  confirmDialog,
  CustomButton,
  CustomImage,
  CustomPagination,
  CustomTable,
  successDialog,
  Text,
  useText,
} from '@src/components';

import {
  getInstructionsPageList,
  instructionDelete,
  instructionUpdate,
  setActiveStep,
} from '@src/store/slice/autoUploadInstructionsSlice';

import { moneyFormat } from '@src/utils/utils';
import { useMediaQuery } from '@src/hooks/useMediaQuery';

import dayjs from 'dayjs';

import { DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';

import '@src/styles/autoUploadInstruction/list.scss';
import cardsRegistered from '@src/assets/icons/icon-cards-registered.svg';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) => {
    return expanded ? (
      <UpCircleOutlined
        className="autoUploadInstructionList-up-circle-outlined"
        style={{ fontSize: '24px' }}
        onClick={(e) => onExpand(record, e)}
      />
    ) : (
      <DownCircleOutlined
        className="autoUploadInstructionList-down-circle-outlined"
        style={{ fontSize: '24px' }}
        onClick={(e) => onExpand(record, e)}
      />
    );
  };
}

export const isVisible = (startDay, nextMonth) => {
  const currentDate = dayjs().date();
  const currentMonth = dayjs().month() + 1;

  if (currentDate >= startDay && currentMonth === nextMonth) {
    return true;
  }

  return false;
};

function getExpandedRowRender(handleUpdate, handleApprovedAutoUpload, handleStatus, handleDelete) {
  return (record) => {
    return (
      <div className="custom-expanded-row">
        <div className="colum-title">
          <span>
            <Text t="orderPrice" />:
          </span>
          <span>{record.totalPrice}₺</span>
        </div>
        <div className="colum-title">
          <span>
            <Text t="status" />:
          </span>
          <span>
            {record?.isOrderCreated === 0 &&
              record?.confirmEveryMonth === 1 &&
              isVisible(record.startDay, record.nextMonth) &&
              record?.isAuthorizationed ? (
              <Text t="waitingApproval" />
            ) : (
              <Text t="approved" />
            )}
          </span>
        </div>
        <div className="actions">
          {record?.isOrderCreated === 0 &&
            record?.confirmEveryMonth === 1 &&
            isVisible(record.startDay, record.nextMonth) &&
            record?.isAuthorizationed ? (
            <CustomButton
              id="ticketRestaurant-autoUploadInstructions-custom-btn-success"
              onClick={() => handleApprovedAutoUpload(record)}
              className="custom-btn-orange"
              buttonAuthName="AutoLoadingApprovalAuthorizationGroup"
            >
              <span>
                <Text t="approveInstruction" />
              </span>
            </CustomButton>
          ) : null}

          <CustomButton
            id="ticketRestaurant-autoUploadInstructions-custom-btn-primary"
            data-testid="update"
            onClick={async () => {
              await handleUpdate(record.id);
            }}
            className="custom-btn-blue"
            buttonAuthName="AutoUploadInstructionsEdit"
          >
            <span>
              <Text t="edit" />
            </span>
          </CustomButton>

          <CustomButton
            id={`ticketRestaurant-autoUploadInstructions-custom-btn-${record.isActive ? 'gray' : 'orange'
              }}`}
            data-testid={`status-${record.isActive ? 'passive' : 'active'}`}
            onClick={() => handleStatus(record)}
            className={`custom-btn-${record.isActive ? 'gray' : 'orange'}`}
            buttonAuthName="AutoUploadInstructionsActivePassive"
          >
            <span>{record.isActive ? <Text t="deactivate" /> : <Text t="activate" />}</span>
          </CustomButton>

          <CustomButton
            data-testid="delete"
            onClick={() => handleDelete(record)}
            className="custom-btn-error"
            buttonAuthName="AutoUploadInstructionsDelete"
          >
            <span>
              <Text t="cancel3" />
            </span>
          </CustomButton>
        </div>
      </div>
    );
  };
}

export default function List() {
  const isMobil = useMediaQuery({ query: '(max-width: 767.98px)' });

  const dispatch = useDispatch();
  const history = useHistory();

  const [expandedRowsInvocies, setExpandedRowsInvocies] = useState([]);

  const { list, tableProperty } = useSelector((state) => state?.autoUploadInstructions);

  // 1. Move hook calls to the top level of the component
  const giveUpCancellationText = useText('giveUpCancellationText');
  const yesText = useText('yes');
  const noText = useText('no');

  const handleTableChange = useCallback(
    async (pageNumber, pageSize) => {
      await dispatch(getInstructionsPageList({ body: { vouId: 1 }, pageNumber, pageSize }));
    },
    [dispatch],
  );

  const handleDelete = async (record) => {
    const body = {
      id: record?.id,
    };

    confirmDialog({
      title: <Text t="attention" />,
      // 2. Use the pre-calculated variables here
      htmlContent: <Text t={giveUpCancellationText} />,
      okText: <Text t={yesText} />,
      cancelText: <Text t={noText} />,
      onOk: async () => {
        const action = await dispatch(instructionDelete(body));

        if (instructionDelete.fulfilled.match(action)) {
          successDialog({
            title: <Text t="success" />,
            message: action?.payload?.message || <Text t="successfullySent" />,
            onOk: async () => {
              await dispatch(getInstructionsPageList({ body: { vouId: 1 } }));
            },
          });
        }
      },
    });
  };

  const handleStatus = async (record) => {
    const body = {
      id: 0,
      integrationId: record?.id,
      isActive: !record.isActive,
      startDay: record.startDay,
      confirmEveryMonth: record.confirmEveryMonth,
      isActiveByUser: true,
      periodType: record?.periodType,
    };

    const action = await dispatch(instructionUpdate(body));

    if (instructionUpdate.fulfilled.match(action)) {
      successDialog({
        title: <Text t="success" />,
        message: action?.payload?.message || <Text t="successfullySent" />,
        onOk: async () => {
          await dispatch(getInstructionsPageList({ body: { vouId: 1 } }));
        },
      });
    }
  };

  const handleApprovedAutoUpload = async (item) => {
    await history.push(
      `/ticket-restaurant/auto-upload-instructions/confirm-instruction?instructionId=${item.id}`,
    );
  };

  const handleUpdate = async (id) => {
    await dispatch(setActiveStep(0));
    await history.push(
      `/ticket-restaurant/auto-upload-instructions/edit-instruction?updateId=${id}`,
    );
  };

  const columns = [
    {
      title: <Text t="balanceActivation" />,
      dataIndex: 'startDay',
      key: 'startDay',
      width: '40%',
      render: (_, record) => {
        return (
          <span>
            <Text t="ofEachMonth" /> {record.startDay}
            <Text t="day2" />
          </span>
        );
      },
    },
    {
      title: <Text t="totalCardsUpperCase" />,
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: '20%',
    },
  ];

  !isMobil &&
    columns.push(
      {
        title: <Text t="orderPrice" />,
        dataIndex: 'totalPrice',
        key: 'totalPrice',
        width: '20%',
        render: (_, record) => {
          return <span>{moneyFormat(record.totalPrice || 0)}</span>;
        },
      },
      {
        title: <Text t="status" />,
        dataIndex: 'isOrderCreated',
        key: 'isOrderCreated',
        width: '20%',
        render: (_, record) => {
          return (
            <span>
              {record?.isOrderCreated === 0 &&
                record?.confirmEveryMonth === 1 &&
                isVisible(record.startDay, record.nextMonth) &&
                record?.isAuthorizationed ? (
                <Text t="waitingApproval" />
              ) : (
                '-'
              )}
            </span>
          );
        },
      },
      {
        title: '',
        dataIndex: '',
        key: 'action',
        align: 'center',
        width: '40%',
        render: (_, record) => {
          return (
            <div className="actions">
              {record?.isOrderCreated === 0 &&
                record?.confirmEveryMonth === 1 &&
                !!record?.isActive &&
                isVisible(record.startDay, record.nextMonth) &&
                record?.isAuthorizationed ? (
                <CustomButton
                  id="ticketRestaurant-autoUploadInstructions-custom-btn-success"
                  onClick={() => handleApprovedAutoUpload(record)}
                  className="custom-btn-orange"
                  buttonAuthName="AutoLoadingApprovalAuthorizationGroup"
                >
                  <span>
                    <Text t="approveInstruction" />
                  </span>
                </CustomButton>
              ) : null}

              <CustomButton
                id="ticketRestaurant-autoUploadInstructions-custom-btn-primary"
                data-testid="update"
                onClick={async () => {
                  await handleUpdate(record.id);
                }}
                className="custom-btn-blue"
                buttonAuthName="AutoUploadInstructionsEdit"
              >
                <span>
                  <Text t="edit" />
                </span>
              </CustomButton>

              <CustomButton
                id={`ticketRestaurant-autoUploadInstructions-custom-btn-${record.isActive ? 'gray' : 'orange'
                  }`}
                data-testid={`status-${record.isActive ? 'passive' : 'active'}`}
                onClick={() => handleStatus(record)}
                className={`custom-btn-${record.isActive ? 'gray' : 'orange'}`}
                buttonAuthName="AutoUploadInstructionsActivePassive"
              >
                <span>{record.isActive ? <Text t="deactivate" /> : <Text t="activate" />}</span>
              </CustomButton>

              <CustomButton
                data-testid="delete"
                onClick={() => handleDelete(record)}
                className="custom-btn-error"
                buttonAuthName="AutoUploadInstructionsDelete"
              >
                <span>
                  <Text t="cancel3" />
                </span>
              </CustomButton>
            </div>
          );
        },
      },
    );

  return (
    <div className="autoUploadInstructions-list">
      <CustomTable
        pagination={false}
        columns={columns}
        dataSource={list}
        rowKey={(record) => `${record?.id}`}
        expandable={{
          showExpandColumn: true,
          expandIconColumnIndex: 37,
          onExpand: (expanded, record, key) =>
            expandedRowsInvocies === key
              ? setExpandedRowsInvocies(null)
              : setExpandedRowsInvocies(key),
          expandedRowRender: isMobil
            ? getExpandedRowRender(
              handleUpdate,
              handleApprovedAutoUpload,
              handleStatus,
              handleDelete,
            )
            : null,
          expandIcon: isMobil ? getExpandIcon() : null,
        }}
      />
      <div className="list-footer">
        <CustomPagination
          data-testid="pagination"
          current={tableProperty?.currentPage}
          defaultCurrent={1}
          pageSize={tableProperty?.pageSize}
          total={tableProperty?.totalCount}
          onChange={async (pageNumber, pageSize) => {
            await handleTableChange(pageNumber, pageSize);
          }}
        />

        {!isMobil && (
          <div className="total-orders-number">
            <CustomImage src={cardsRegistered} />
            <div className="count-title">
              <Text t="total" />
              <b>{tableProperty?.totalCount}</b> <Text t="orderTitle" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}